import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Copy, Check, ExternalLink } from 'lucide-react';

interface ReadyzResponse {
  status: string;
  version: string;
  shortCommit: string;
}

// Fixed URL — the ACA hostname is deterministic from the Bicep deployment.
// (Once we move to mcp.heuristichero.com it'll be hardcoded here too.)
const MCP_URL = 'https://aca-mcp-heuristic-hero.victoriouscoast-22a18d0e.eastus2.azurecontainerapps.io/mcp';

export function Mcp() {
  const [copied, setCopied] = useState(false);

  const ready = useQuery<ReadyzResponse>({
    queryKey: ['mcp-ready'],
    queryFn: async () => {
      const r = await fetch(MCP_URL.replace('/mcp', '/readyz'));
      if (!r.ok) throw new Error('not ready');
      return r.json();
    },
    refetchInterval: 30_000,
    retry: false,
  });

  function copy() {
    navigator.clipboard.writeText(MCP_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-12">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold tracking-tight">MCP server</h1>
        <p className="mt-2 text-[var(--color-muted-foreground)]">
          Heuristic Hero exposes a remote{' '}
          <a href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer"
             className="text-[var(--color-primary)] hover:underline">Model Context Protocol</a>
          {' '}server. Add the URL below to any MCP-aware client (Claude Cowork, Claude Desktop,
          Claude Code, Cursor, …) and you can ask the assistant to evaluate any URL inline —
          including on a recurring schedule, in clients that support that.
        </p>
      </div>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Server URL</CardTitle>
          <CardDescription>
            No auth. Streamable-HTTP transport.{' '}
            {ready.data ? (
              <Badge variant="default" className="ml-1">live · v{ready.data.version}</Badge>
            ) : (
              <Badge variant="secondary" className="ml-1">checking…</Badge>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/30 px-3 py-2">
            <code className="flex-1 truncate font-mono text-sm">{MCP_URL}</code>
            <button
              onClick={copy}
              className="inline-flex items-center gap-1 rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm text-[var(--color-primary-foreground)] hover:opacity-90"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'copied' : 'copy'}
            </button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Tools the assistant can call</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li>
              <code className="font-mono text-[var(--color-primary)]">evaluate_url(url, designMd?, sourceUrl?)</code> — start an evaluation, returns a <code className="font-mono">jobId</code>
            </li>
            <li>
              <code className="font-mono text-[var(--color-primary)]">get_status(jobId)</code> — poll until <code className="font-mono">status</code> is 2 (Completed) or 3 (Failed)
            </li>
            <li>
              <code className="font-mono text-[var(--color-primary)]">get_summary(jobId)</code> — short, action-oriented Markdown digest (the part you'd paste into Slack)
            </li>
            <li>
              <code className="font-mono text-[var(--color-primary)]">get_report(jobId)</code> — full structured response with NN/g per-heuristic scores, axe violations, percentile, cost
            </li>
          </ul>
          <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
            Plus MCP resources for direct artifact reads:{' '}
            <code className="font-mono">hh://job/{'{jobId}'}/summary</code>{' '}
            and <code className="font-mono">hh://job/{'{jobId}'}/report</code>.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Use case: nightly UX gate via Claude Cowork</CardTitle>
          <CardDescription>End-to-end no-code automation.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <ol className="list-decimal space-y-1 pl-6">
            <li>In Claude Cowork, add this MCP server URL.</li>
            <li>Say in plain language: <em>"every night at 2am, evaluate https://my-app.com against the DESIGN.md in https://github.com/me/my-repo and email me the summary."</em></li>
            <li>Cowork schedules the recurring task. Each night it calls <code className="font-mono">evaluate_url</code>, polls <code className="font-mono">get_status</code>, then reads <code className="font-mono">get_summary</code> and emails it to you.</li>
          </ol>
          <p className="text-[var(--color-muted-foreground)]">
            Same flow works in Claude Desktop / Claude Code / Cursor on demand (without scheduling).
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Limits</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1 text-sm">
            <li><strong>50 evaluations / hour / IP</strong> — same shared cap across all unauthenticated surfaces (web + MCP).</li>
            <li><strong>Backend model:</strong> <code className="font-mono">gpt-4o</code> for the unauthenticated tier (fixed). Authenticated tiers will choose models in v0.2.</li>
            <li><strong>No client auth required</strong> — you can paste the URL into any MCP client and it works. Sign-in / per-user quotas land later.</li>
          </ul>
          <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
            <a
              href="https://modelcontextprotocol.io/specification"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 hover:text-[var(--color-primary)]"
            >
              <ExternalLink className="h-3 w-3" /> MCP specification
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
